<template>
  <div class="feature-selector">
    <h3 class="feature-selector__title">✨ {{ t("select_feature") }}</h3>
    <div class="feature-selector__grid">
      <div
        v-for="feature in features"
        :key="feature.value"
        class="feature-option"
        :class="{
          'feature-option--selected': selectedFeature === feature.value,
        }"
        @click="selectFeature(feature.value)"
      >
        <div class="feature-option__icon" v-html="feature.icon"></div>
        <div class="feature-option__content">
          <span class="feature-option__label">{{ feature.label }}</span>
          <span class="feature-option__description">{{
            feature.description
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { MiaFeatureType } from "../../types";
import { useI18n } from "../../i18n";

export default {
  name: "FeatureSelector",
  props: {
    selectedFeature: {
      type: String,
      default: null,
    },
  },
  emits: ["select"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const features = computed(() => [
      {
        value: MiaFeatureType.PROBLEM_SOLVING,
        label: t("problem_solving"),
        description: t("problem_solving_short_desc"),
        icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M9 3h6M10 3v6.5L5 19h14l-5-9.5V3" />
        </svg>`,
      },
      {
        value: MiaFeatureType.QBANK,
        label: t("qbank"),
        description: t("qbank_short_desc"),
        icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>`,
      },
      {
        value: MiaFeatureType.SUMMARY,
        label: t("summaries"),
        description: t("summaries_short_desc"),
        icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>`,
      },
      {
        value: MiaFeatureType.FLASH_CARDS,
        label: t("flash_cards"),
        description: t("flash_cards_short_desc"),
        icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polygon points="12,2 2,7 12,12 22,7 12,2" />
          <polyline points="2,17 12,22 22,17" />
          <polyline points="2,12 12,17 22,12" />
        </svg>`,
      },
    ]);

    const selectFeature = (feature) => {
      emit("select", feature);
    };

    return {
      t,
      features,
      selectFeature,
    };
  },
};
</script>

<style scoped>
.feature-selector {
  padding: 0 12px;
}

.feature-selector__title {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 12px 0;
}

.feature-selector__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.feature-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e5e7eb;
}

.feature-option:hover {
  border-color: rgba(0, 196, 68, 0.5);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.feature-option--selected {
  border-color: var(--mia-primary);
  background: linear-gradient(
    145deg,
    rgba(0, 196, 68, 0.1),
    rgba(0, 196, 68, 0.16)
  );
}

.feature-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(0, 196, 68, 0.12);
  border-radius: 12px;
  color: var(--mia-primary);
  flex-shrink: 0;
}

.feature-option--selected .feature-option__icon {
  background: rgba(0, 196, 68, 0.2);
}

.feature-option__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.feature-option__label {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.feature-option__description {
  font-size: 12px;
  color: #6b7280;
}

@media (max-width: 640px) {
  .feature-selector__grid {
    grid-template-columns: 1fr;
  }
}
</style>
